import { create } from 'zustand'
import { useBetSliderStore } from './betSliderStore'

export const ULTRA_PLAY_SPIN_OPTIONS = [10, 20, 30, 40, 50, 70, 100, 500, 1000, -1]

export type UltraPlayStatus = 'READY' | 'PLAYING' | 'PAUSED'
export type UltraPlaySpinType = 'NORMAL' | 'FREE_SPIN' | 'CASCADE' | 'BONUS'

export type UltraPlaySubSpin = {
    win: number
    winMultiplier: number
    balance: number
    type: UltraPlaySpinType
    freeSpinsAwarded?: number
}

export type UltraPlaySpin = {
    spinNumber: number
    bet: number
    win: number
    winMultiplier: number
    balance: number
    type: UltraPlaySpinType
    freeSpinsAwarded?: number
    subSpins?: UltraPlaySubSpin[]
    isExpanded: boolean
}

type UltraPlayStore = {
    isOpen: boolean
    status: UltraPlayStatus
    selectedBetIndex: number
    selectedSpinsIndex: number
    remainingSpins: number
    currentSpinNumber: number
    totalBet: number
    totalWin: number
    startingBalance: number
    lastBalance: number
    spins: UltraPlaySpin[]
    stopOnFeature: boolean
    selectedBuyBonusFeatureName: string
    showFeatureChoiceDialog: boolean
    featureDialogAutoplayable: boolean
    isInFeature: boolean
    previousSpeedLevel: number
    pendingAutoplayRestart: boolean
    showInsufficientBalance: boolean

    open: () => void
    close: () => void
    setStatus: (status: UltraPlayStatus) => void
    setSelectedBetIndex: (index: number) => void
    setSelectedSpinsIndex: (index: number) => void
    setStopOnFeature: (stop: boolean) => void
    setSelectedBuyBonusFeatureName: (name: string) => void
    addSpin: (spin: Omit<UltraPlaySpin, 'isExpanded'>, expanded?: boolean) => void
    addSubSpin: (spinNumber: number, subSpin: UltraPlaySubSpin) => void
    updateSpin: (spinNumber: number, partial: Partial<UltraPlaySpin>) => void
    setSpinExpanded: (spinNumber: number, expanded: boolean) => void
    start: (startingBalance: number, remainingSpins: number) => void
    // Resume a finished session: keep the table, totals and spin counter; just add more spins.
    continueSession: (remainingSpins: number) => void
    pause: () => void
    resume: () => void
    stop: () => void
    setShowFeatureChoiceDialog: (show: boolean) => void
    setShowInsufficientBalance: (show: boolean) => void
    setFeatureDialogAutoplayable: (autoplayable: boolean) => void
    setIsInFeature: (inFeature: boolean) => void
    toggleSpinExpanded: (spinNumber: number) => void
    decrementRemainingSpins: () => void
    extendRemainingSpins: (extraSpins: number) => void
    reset: () => void
    setPreviousSpeedLevel: (level: number) => void
    setPendingAutoplayRestart: (v: boolean) => void
}

export const useUltraPlayStore = create<UltraPlayStore>((set) => ({
    isOpen: false,
    status: 'READY',
    selectedBetIndex: 0,
    selectedSpinsIndex: 1,
    remainingSpins: 0,
    currentSpinNumber: 0,
    totalBet: 0,
    totalWin: 0,
    startingBalance: 0,
    lastBalance: 0,
    spins: [],
    stopOnFeature: false,
    selectedBuyBonusFeatureName: 'OFF',
    showFeatureChoiceDialog: false,
    featureDialogAutoplayable: false,
    isInFeature: false,
    previousSpeedLevel: 0,
    pendingAutoplayRestart: false,
    showInsufficientBalance: false,

    open: () => set({ isOpen: true, selectedBetIndex: useBetSliderStore.getState().activeIndex ?? 0 }),
    close: () => set({ isOpen: false }),
    setStatus: (status) => set({ status }),
    setSelectedBetIndex: (index) => set({ selectedBetIndex: index }),
    setSelectedSpinsIndex: (index) => set({ selectedSpinsIndex: index }),
    setStopOnFeature: (stopOnFeature) => set({ stopOnFeature }),
    setSelectedBuyBonusFeatureName: (name) => set({ selectedBuyBonusFeatureName: name }),
    addSpin: (spin, expanded = false) =>
        set((state) => ({
            spins: [{ ...spin, isExpanded: expanded }, ...state.spins],
            currentSpinNumber: spin.spinNumber,
            totalBet: state.totalBet + spin.bet,
            totalWin: state.totalWin + spin.win,
            lastBalance: spin.balance,
        })),
    addSubSpin: (spinNumber, subSpin) =>
        set((state) => ({
            spins: state.spins.map((s) =>
                s.spinNumber === spinNumber ? { ...s, subSpins: [...(s.subSpins ?? []), subSpin] } : s
            ),
            totalWin: state.totalWin + subSpin.win,
            lastBalance: subSpin.balance,
        })),
    updateSpin: (spinNumber, partial) =>
        set((state) => ({
            spins: state.spins.map((s) => (s.spinNumber === spinNumber ? { ...s, ...partial } : s)),
        })),
    setSpinExpanded: (spinNumber, expanded) =>
        set((state) => ({
            spins: state.spins.map((s) => (s.spinNumber === spinNumber ? { ...s, isExpanded: expanded } : s)),
        })),
    start: (startingBalance, remainingSpins) =>
        set({
            status: 'PLAYING',
            startingBalance,
            lastBalance: startingBalance,
            remainingSpins,
            currentSpinNumber: 0,
            totalBet: 0,
            totalWin: 0,
            spins: [],
            isInFeature: false,
            showInsufficientBalance: false,
        }),
    continueSession: (remainingSpins) =>
        set({ status: 'PLAYING', remainingSpins, isInFeature: false, showInsufficientBalance: false }),
    pause: () => set({ status: 'PAUSED' }),
    resume: () => set({ status: 'PLAYING' }),
    stop: () => set({ status: 'READY', remainingSpins: 0, isInFeature: false }),
    setShowFeatureChoiceDialog: (show) => set({ showFeatureChoiceDialog: show }),
    setShowInsufficientBalance: (show) => set({ showInsufficientBalance: show }),
    setFeatureDialogAutoplayable: (autoplayable) => set({ featureDialogAutoplayable: autoplayable }),
    setIsInFeature: (inFeature) => set({ isInFeature: inFeature }),
    toggleSpinExpanded: (spinNumber) =>
        set((state) => ({
            spins: state.spins.map((s) => (s.spinNumber === spinNumber ? { ...s, isExpanded: !s.isExpanded } : s)),
        })),
    decrementRemainingSpins: () => set((state) => ({ remainingSpins: Math.max(0, state.remainingSpins - 1) })),
    extendRemainingSpins: (extraSpins) => set((state) => ({ remainingSpins: state.remainingSpins + extraSpins })),
    reset: () =>
        set({
            status: 'READY',
            remainingSpins: 0,
            currentSpinNumber: 0,
            totalBet: 0,
            totalWin: 0,
            lastBalance: 0,
            // Clear so the setup screen shows the live balance on reopen, not the previous session's start.
            startingBalance: 0,
            spins: [],
            showFeatureChoiceDialog: false,
            featureDialogAutoplayable: false,
            isInFeature: false,
            selectedBuyBonusFeatureName: 'OFF',
            showInsufficientBalance: false,
        }),
    setPreviousSpeedLevel: (level) => set({ previousSpeedLevel: level }),
    setPendingAutoplayRestart: (v) => set({ pendingAutoplayRestart: v }),
}))
